// src/slices/ticketReturnWarehouseReport/reducer.ts
// Redux slice cho US-019 — Lưu trữ vật lý & báo cáo nhanh thu hồi vé ế

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TicketReturnReconciliation,
  TicketReturnReportRow,
  TicketReturnReportSummary,
} from 'slices/ticketReturnReconciliation/types';
import {
  TicketReturnReportFilters,
  TicketReturnWarehouseModalState,
  TicketReturnWarehouseReportState,
} from './types';
import {
  fetchApprovedReconciliations,
  setWarehouseLocation,
  fetchTicketReturnReport,
  exportTicketReturnReport,
} from './thunk';

const initialFilters: TicketReturnReportFilters = {
  issuancePlanId: '',
  agentId: '',
};

const emptySummary: TicketReturnReportSummary = {
  issuedQuantity: 0,
  returnedQuantity: 0,
  rejectedQuantity: 0,
  returnRatePercent: 0,
};

/**
 * BE trả mảng phẳng TicketReturnReportRowDto[] (không kèm khối summary) nên FE tự cộng tổng.
 * Tỷ lệ hoàn tính lại trên tổng (returned/issued) chứ KHÔNG cộng dồn returnRatePercent từng dòng —
 * cộng phần trăm của các dòng có mẫu số khác nhau là sai về mặt số học.
 */
const buildSummary = (rows: TicketReturnReportRow[]): TicketReturnReportSummary => {
  const totals = rows.reduce<Omit<TicketReturnReportSummary, 'returnRatePercent'>>(
    (acc, row) => ({
      issuedQuantity: acc.issuedQuantity + (row.issuedQuantity || 0),
      returnedQuantity: acc.returnedQuantity + (row.returnedQuantity || 0),
      rejectedQuantity: acc.rejectedQuantity + (row.rejectedQuantity || 0),
    }),
    { issuedQuantity: 0, returnedQuantity: 0, rejectedQuantity: 0 },
  );

  const returnRatePercent = totals.issuedQuantity
    ? Math.round((totals.returnedQuantity / totals.issuedQuantity) * 10000) / 100
    : 0;

  return { ...totals, returnRatePercent };
};

const initialState: TicketReturnWarehouseReportState = {
  list: [],
  selectedItem: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  reportRows: [],
  reportSummary: { ...emptySummary },
  filters: { ...initialFilters },
  loading: false,
  loadingReport: false,
  submitting: false,
  exporting: false,
  error: null,
  modalState: {
    warehouseLocation: false,
  },
};

const ticketReturnWarehouseReportSlice = createSlice({
  name: 'TicketReturnWarehouseReport',
  initialState,
  reducers: {
    setSelectedItem: (state, action: PayloadAction<TicketReturnReconciliation | null>) => {
      state.selectedItem = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<TicketReturnReportFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.pagination.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof TicketReturnWarehouseModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketReturnWarehouseModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Danh sách phiếu đã thẩm định
      .addCase(fetchApprovedReconciliations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovedReconciliations.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.list = payload?.items || payload?.data || payload || [];
        state.pagination.totalItems = payload?.totalItems ?? payload?.totalCount ?? state.list.length;
        state.pagination.totalPages =
          payload?.totalPages ??
          Math.ceil(state.pagination.totalItems / Math.max(state.pagination.pageSize, 1));
      })
      .addCase(fetchApprovedReconciliations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Gán vị trí kho lưu trữ — API chỉ trả id nên nơi gọi phải refetch
      .addCase(setWarehouseLocation.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(setWarehouseLocation.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.warehouseLocation = false;
        state.selectedItem = null;
      })
      .addCase(setWarehouseLocation.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Báo cáo nhanh
      .addCase(fetchTicketReturnReport.pending, (state) => {
        state.loadingReport = true;
        state.error = null;
      })
      .addCase(fetchTicketReturnReport.fulfilled, (state, action) => {
        state.loadingReport = false;
        const payload = action.payload as any;
        state.reportRows = Array.isArray(payload) ? payload : payload?.items || payload?.data || [];
        state.reportSummary = buildSummary(state.reportRows);
      })
      .addCase(fetchTicketReturnReport.rejected, (state, action) => {
        state.loadingReport = false;
        state.reportRows = [];
        state.reportSummary = { ...emptySummary };
        state.error = action.payload as string;
      })

      // Export
      .addCase(exportTicketReturnReport.pending, (state) => {
        state.exporting = true;
      })
      .addCase(exportTicketReturnReport.fulfilled, (state) => {
        state.exporting = false;
      })
      .addCase(exportTicketReturnReport.rejected, (state, action) => {
        state.exporting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedItem,
  setPage,
  setFilters,
  clearFilters,
  clearError,
  openModal,
  closeModal,
} = ticketReturnWarehouseReportSlice.actions;

export default ticketReturnWarehouseReportSlice.reducer;
